import { ViewTimeInference } from "@/dataprocessor/view-time";
import { SectionScoreType } from "./types";

export const SectionScoringHandler = {
  setup() {},

  getPrevSessionInfo() {
    const prevSessionInfo = JSON.parse(
      localStorage.getItem(`pulse-prev-session`) || "{}",
    ) as { sectionsViewed: string[]; time: number; sessionId: string };

    return Object.keys(prevSessionInfo).length > 0 ? prevSessionInfo : null;
  },

  persistSessionInfo() {
    localStorage.setItem(
      `pulse-prev-session`,
      JSON.stringify({
        sectionsViewed: Object.keys(ViewTimeInference.seenSection),
        time: Date.now(),
        sessionId: window.helium.sessionToken,
      }),
    );
  },

  persistScore(sectionId: string, score: SectionScoreType) {
    sessionStorage.setItem(
      `score-section`,
      JSON.stringify(
        Object.assign({}, SectionScoringHandler.getSectionScores(), {
          [sectionId]: score,
        }),
      ),
    );
  },

  getRepeatScore(sectionId: string) {
    const {
      sectionsViewed = [],
      time,
      sessionId,
    } = SectionScoringHandler.getPrevSessionInfo() || {
      sectionsViewed: [],
      sessionId: "",
      time: 0,
    };

    const oneDay = 1000 * 60 * 60 * 24;
    const twoDays = oneDay * 2;
    const oneWeek = oneDay * 7;

    if (!time) return 0;

    if (
      window.helium.sessionToken === sessionId ||
      !sectionsViewed.includes(sectionId) ||
      Date.now() - time > oneWeek
    ) {
      return 0;
    }

    const timeAgo = Date.now() - time;

    if (timeAgo <= oneDay) {
      return 15;
    }

    if (timeAgo <= twoDays) {
      return 10;
    }

    return 5;
  },

  getSectionScores() {
    return JSON.parse(
      sessionStorage.getItem(`score-section`) || "{}",
    ) as Record<string, SectionScoreType>;
  },

  getScore(sectionId: string): SectionScoreType {
    const currentScore = SectionScoringHandler.getSectionScores()[sectionId];

    const element = document.querySelector(`section[id*="${sectionId}"]`);

    const scrollDepthOfElement = Math.abs(
      element ? element.getBoundingClientRect().top + window.scrollY : 0,
    );

    let scrollDepthBiasScoreFinal = 0;

    if (scrollDepthOfElement <= 500) {
      scrollDepthBiasScoreFinal = 5;
    } else if (scrollDepthOfElement <= 1000) {
      scrollDepthBiasScoreFinal = 10;
    } else if (scrollDepthOfElement <= 3000) {
      scrollDepthBiasScoreFinal = 15;
    } else {
      scrollDepthBiasScoreFinal = 20;
    }

    const defaultObject = {
      intentViaInteraction: [],
      intentViaTime: 0,
      pageBias:
        window?.helium?.pageObj?.[window.location.pathname]?.sectionPageBias ||
        0,
      repeatWithinWindow: SectionScoringHandler.getRepeatScore(sectionId),
      scrollDepthBias: scrollDepthBiasScoreFinal,
      sectionsInView: [],
    };

    if (!currentScore || Object.keys(currentScore).length === 0) {
      return defaultObject;
    }

    return currentScore;
  },

  computeScore(sectionId: string | SectionScoreType) {
    const currentScore =
      typeof sectionId === "string"
        ? SectionScoringHandler.getScore(sectionId)
        : sectionId;

    let intentViaTimeScore = 0;

    if (currentScore.intentViaTime < 3000) {
      intentViaTimeScore = 0;
    } else if (currentScore.intentViaTime < 20000) {
      intentViaTimeScore = 5;
    } else if (currentScore.intentViaTime < 30000) {
      intentViaTimeScore = 10;
    } else {
      intentViaTimeScore = 15;
    }

    let intentViaInteractionScore = 0;

    const clickThroughs = currentScore.intentViaInteraction.filter(
      (interaction) => interaction === "click",
    ).length;
    const zooms = currentScore.intentViaInteraction.filter(
      (interaction) => interaction === "zoom",
    ).length;

    if (clickThroughs === 0) {
      intentViaInteractionScore = 0;
    } else if (clickThroughs < 2) {
      intentViaInteractionScore = 10;
    } else if (clickThroughs === 2) {
      intentViaInteractionScore = 15;
    } else {
      intentViaInteractionScore = 20;
    }

    if (zooms === 0) {
      intentViaInteractionScore += 0;
    } else if (zooms < 2) {
      intentViaInteractionScore += 10;
    } else if (zooms === 2) {
      intentViaInteractionScore += 15;
    } else {
      intentViaInteractionScore += 20;
    }

    intentViaInteractionScore = intentViaInteractionScore / 2;

    const pageBiasScore = currentScore.pageBias;

    const sectionsInViewScore =
      currentScore.sectionsInView.reduce((acc, sectionsInView) => {
        return acc + sectionsInView;
      }, 0) / currentScore.sectionsInView.length;

    let sectionsInViewScoreFinal = 0;

    if (sectionsInViewScore === 0) {
      sectionsInViewScoreFinal = 10;
    } else if (sectionsInViewScore < 3) {
      sectionsInViewScoreFinal = 5;
    } else {
      sectionsInViewScoreFinal = 1;
    }

    const scrollDepthBiasScore = currentScore.scrollDepthBias;

    const repeatWithinWindowScore = currentScore.repeatWithinWindow;

    return (
      intentViaTimeScore +
      intentViaInteractionScore +
      pageBiasScore +
      sectionsInViewScoreFinal +
      scrollDepthBiasScore +
      repeatWithinWindowScore
    );
  },

  scoreFromView(
    sectionId: string,
    props: {
      timeSpent: number;
      sectionsInView: number;
    },
  ) {
    const currentScore = SectionScoringHandler.getScore(sectionId);

    const newScore = {
      ...currentScore,
      intentViaTime: props.timeSpent + currentScore.intentViaTime,
      sectionsInView: [...currentScore.sectionsInView, props.sectionsInView],
    };

    SectionScoringHandler.persistScore(sectionId, newScore);

    SectionScoringHandler.persistSessionInfo();

    return SectionScoringHandler.computeScore(newScore);
  },

  scoreFromInteraction(
    sectionId: string,
    props: { interaction: "click" | "zoom" },
  ) {
    const currentScore = SectionScoringHandler.getScore(sectionId);

    const newScore = {
      ...currentScore,
      intentViaInteraction: [
        ...currentScore.intentViaInteraction,
        props.interaction,
      ],
    };

    SectionScoringHandler.persistScore(sectionId, newScore);

    SectionScoringHandler.persistSessionInfo();

    return SectionScoringHandler.computeScore(newScore);
  },
};
